import React, { Suspense, lazy, useRef, useState } from 'react'
import { Box, ButtonBase, Popover, Skeleton, Tooltip } from '@material-ui/core'
import { BarChart as ChartGlyphIcon } from '@material-ui/icons'
import { useAnalysisStatsSummary, formatCount } from 'src/views/AnalysisView/useAnalysisStats'

// Loaded on first open, so the chart library stays out of the layout bundle.
const AnalysisHistoryChart = lazy(() => import('src/views/AnalysisView/AnalysisHistoryChart'))

// Menu-bar style summary of analysis statistics, like a macOS stats extra: a small
// sparkline, the running total and this month's count. Clicking it opens the full
// statistics panel under it. It reads the shared react-query hooks, so the panel
// opens with its numbers already in place.

const SPARK_WIDTH = 42
const SPARK_HEIGHT = 16
const BAR_GAP = 1

const PANEL_ID = 'analysis-stats-panel'

// One thin bar per day, oldest on the left. Empty days keep a 1px tick so the
// glyph still reads as a chart when nothing ran. Until the daily numbers arrive (or
// if they fail) a plain chart icon stands in, so no activity is implied.
function Sparkline({ values }) {
  if (!values.length) {
    return <ChartGlyphIcon aria-hidden="true" sx={{ fontSize: 18, flexShrink: 0 }} />
  }
  const count = values.length
  const barWidth = (SPARK_WIDTH - BAR_GAP * (count - 1)) / count
  const max = Math.max(1, ...values)
  return (
    <Box
      component="svg"
      width={SPARK_WIDTH}
      height={SPARK_HEIGHT}
      viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
      aria-hidden="true"
      sx={{ flexShrink: 0, display: 'block', color: 'inherit' }}
    >
      {Array.from({ length: count }, (_, index) => {
        const value = values[index] || 0
        const height = value > 0 ? Math.max(3, (value / max) * SPARK_HEIGHT) : 1
        return (
          <rect
            key={index}
            x={index * (barWidth + BAR_GAP)}
            y={SPARK_HEIGHT - height}
            width={barWidth}
            height={height}
            rx={value > 0 ? 0.75 : 0}
            fill="currentColor"
            opacity={value > 0 ? 0.9 : 0.35}
          />
        )
      })}
    </Box>
  )
}

export default function StatsSummary() {
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const { total, thisMonth, sparkline } = useAnalysisStatsSummary()

  const hasTotal = total !== null
  const totalLabel = hasTotal ? `${formatCount(total)} ${total === 1 ? 'analysis' : 'analyses'}` : 'Analyses'
  const monthLabel = thisMonth !== null ? `${formatCount(thisMonth)} this month` : null
  const ariaLabel = ['Analysis statistics', hasTotal && totalLabel, monthLabel].filter(Boolean).join(', ')

  return (
    <>
      <Tooltip title="Analysis statistics" disableHoverListener={open}>
        <ButtonBase
          ref={anchorRef}
          onClick={() => setOpen((value) => !value)}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open ? 'true' : 'false'}
          aria-controls={open ? PANEL_ID : undefined}
          sx={{
            height: 32,
            px: 1,
            gap: 1,
            flexShrink: 0,
            borderRadius: 1,
            color: 'text.secondary',
            typography: 'body2',
            fontSize: (theme) => theme.typography.pxToRem(13),
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            transition: (theme) =>
              theme.transitions.create(['background-color', 'color'], {
                duration: theme.transitions.duration.shorter,
              }),
            '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            '&.Mui-focusVisible': { bgcolor: 'action.hover', color: 'text.primary' },
            ...(open && { bgcolor: 'action.selected', color: 'text.primary' }),
          }}
        >
          <Sparkline values={sparkline} />
          <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
              {totalLabel}
            </Box>
            {monthLabel && (
              <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>
                {` · ${monthLabel}`}
              </Box>
            )}
          </Box>
        </ButtonBase>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          id: PANEL_ID,
          role: 'dialog',
          'aria-label': 'Analysis statistics',
          sx: {
            mt: 1,
            width: 560,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: '80vh',
            overflowY: 'auto',
            backgroundImage: 'none',
            borderRadius: (theme) => `${theme.shape.borderRadiusMd}px`,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: (theme) => theme.customShadows.z20,
          },
        }}
      >
        <Suspense fallback={<Skeleton variant="rectangular" height={420} sx={{ m: 2.5, borderRadius: 1 }} />}>
          <AnalysisHistoryChart />
        </Suspense>
      </Popover>
    </>
  )
}
